/**
 * MarketBrewer LLC - Header Component
 *
 * Features:
 * - Dark theme with DC Metro accent colors
 * - Responsive navigation with mobile hamburger menu
 * - Bilingual support (EN/ES)
 * - Language switcher
 * - Sticky header with backdrop blur
 * - CTA button
 * - Accessible navigation
 */

import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import LanguageSwitcher from "@/components/Navigation/LanguageSwitcher";
import Container from "@/components/Shared/Container";

interface HeaderProps {
  language: "en" | "es";
}

interface NavLink {
  to: string;
  label: string;
}

const Header: React.FC<HeaderProps> = ({ language }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Navigation links based on language
  const navLinks: NavLink[] =
    language === "en"
      ? [
          { to: "/", label: "Home" },
          { to: "/services", label: "Services" },
          { to: "/episodes", label: "Episodes" },
          { to: "/about", label: "About" },
          { to: "/contact", label: "Contact" },
        ]
      : [
          { to: "/es", label: "Inicio" },
          { to: "/es/servicios", label: "Servicios" },
          { to: "/es/episodios", label: "Episodios" },
          { to: "/es/sobre-nosotros", label: "Sobre Nosotros" },
          { to: "/es/contacto", label: "Contacto" },
        ];

  const ctaText =
    language === "en" ? "Get a Free Consultation" : "Consulta Gratis";

  // Handle scroll effect for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on escape key
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setMobileMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen, handleKeyDown]);

  // Check if a link is active
  const isActiveLink = (path: string): boolean => {
    if (path === "/" || path === "/es") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-dark-950/95 backdrop-blur-md shadow-lg shadow-black/20"
            : "bg-dark-950"
        }`}
      >
        {/* Metro stripe accent at top */}
        <div className="h-1 w-full bg-gradient-to-r from-metro-red-600 via-metro-orange-500 to-metro-yellow-400" />

        <Container size="xl">
          <nav className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link
              to={language === "en" ? "/" : "/es"}
              className="relative z-10 flex items-center gap-2 group"
              aria-label="MarketBrewer Home"
            >
              <div className="flex flex-col">
                <span className="text-xl lg:text-2xl font-bold tracking-tight text-dark-50 group-hover:text-metro-orange-500 transition-colors duration-300">
                  Market
                  <span className="text-metro-orange-500 group-hover:text-dark-50 transition-colors duration-300">
                    Brewer
                  </span>
                </span>
                <span className="text-[10px] lg:text-xs text-dark-400 tracking-widest uppercase -mt-1">
                  Digital Marketing & Web Dev
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <ul className="flex items-center gap-1">
                {navLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${
                        isActiveLink(link.to)
                          ? "text-metro-orange-500"
                          : "text-dark-300 hover:text-dark-50"
                      }`}
                    >
                      {link.label}
                      {isActiveLink(link.to) && (
                        <motion.div
                          layoutId="activeNavIndicator"
                          className="absolute bottom-0 left-4 right-4 h-0.5 bg-metro-orange-500 rounded-full"
                          transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 30,
                          }}
                        />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Language Switcher */}
              <div className="border-l border-dark-700 pl-6">
                <LanguageSwitcher />
              </div>

              {/* CTA Button */}
              <Link
                to={language === "en" ? "/contact" : "/es/contacto"}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-dark-950 bg-metro-orange-500 rounded-lg hover:bg-metro-orange-400 active:bg-metro-orange-600 transition-colors duration-200 shadow-lg shadow-metro-orange-500/20"
              >
                {ctaText}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                  />
                </svg>
              </Link>
            </div>

            {/* Mobile Controls */}
            <div className="lg:hidden relative z-10 flex items-center gap-3">
              <div className="transition-transform duration-200 ease-out hover:scale-[1.02]">
                <LanguageSwitcher />
              </div>
              <button
                type="button"
                className="p-2 -mr-2 text-dark-300 hover:text-dark-50 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                <div className="w-6 h-6 relative">
                  <span
                    className={`absolute left-0 block w-6 h-0.5 bg-current transform transition-all duration-300 ${
                      mobileMenuOpen ? "top-[11px] rotate-45" : "top-1"
                    }`}
                  />
                  <span
                    className={`absolute left-0 top-[11px] block w-6 h-0.5 bg-current transition-all duration-300 ${
                      mobileMenuOpen ? "opacity-0 translate-x-2" : "opacity-100"
                    }`}
                  />
                  <span
                    className={`absolute left-0 block w-6 h-0.5 bg-current transform transition-all duration-300 ${
                      mobileMenuOpen ? "top-[11px] -rotate-45" : "top-[19px]"
                    }`}
                  />
                </div>
              </button>
            </div>
          </nav>
        </Container>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-dark-950/80 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Mobile Menu Panel */}
            <motion.div
              id="mobile-menu"
              initial={{ opacity: 0, x: "100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm bg-dark-900 shadow-2xl lg:hidden"
              role="dialog"
              aria-modal="true"
              aria-label={language === "en" ? "Mobile menu" : "Menú móvil"}
            >
              {/* Metro stripe at top */}
              <div className="h-1 w-full bg-gradient-to-r from-metro-red-600 via-metro-orange-500 to-metro-yellow-400" />

              <div className="flex flex-col h-full pt-6 pb-6 px-6">
                {/* Top bar with close button */}
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-dark-200">
                    {language === "en" ? "Menu" : "Menú"}
                  </span>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-dark-300 hover:text-dark-50 hover:bg-dark-800/60 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-label={
                      language === "en" ? "Close menu" : "Cerrar menú"
                    }
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 6l12 12M18 6l-12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Top CTA */}
                <div className="mt-5">
                  <Link
                    to={language === "en" ? "/contact" : "/es/contacto"}
                    className="flex items-center justify-center gap-2 w-full px-5 py-3 text-base font-semibold text-dark-950 bg-metro-orange-500 rounded-lg hover:bg-metro-orange-400 active:bg-metro-orange-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {ctaText}
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                      />
                    </svg>
                  </Link>
                </div>
                {/* Navigation Links */}
                <nav className="flex-1 mt-8">
                  <ul className="space-y-1">
                    {navLinks.map((link, index) => (
                      <motion.li
                        key={link.to}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + index * 0.05 }}
                      >
                        <Link
                          to={link.to}
                          className={`block px-4 py-3 text-lg font-medium rounded-lg transition-colors ${
                            isActiveLink(link.to)
                              ? "text-metro-orange-500 bg-dark-800"
                              : "text-dark-200 hover:text-dark-50 hover:bg-dark-800/50"
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {link.label}
                        </Link>
                      </motion.li>
                    ))}
                  </ul>
                </nav>

                <div className="pt-6 border-t border-dark-700" />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer for fixed header */}
      <div className="h-[68px] lg:h-[84px]" />
    </>
  );
};

export default Header;
